import Column from '@amzn/meridian/column';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Textarea from '@amzn/meridian/textarea';
import React, { useCallback, useMemo, useState } from 'react';
import { IAuthorizedReseller } from '../../redux/types/reseller';
import { ResellerClaimRegexPattern } from '../../types';
import { PatternSegment, segmentsToDescription, segmentsToRegex } from '../../utils/PatternEngine';
import { ensureAnchors, validateExamplesMatchPattern, validateRegexPattern } from '../../utils/RegexValidationUtils';
import { Alert, Button, Input, Text, TranslatedString, useTranslation } from '../blocks';
import { PatternBuilder } from './PatternBuilder';
import { PatternTester } from './PatternTester';

interface PatternInputProps {
    regexPattern: string;
    onChange?: (value: string) => void;
    onBlur: () => void;
    disabled: boolean;
    emptyError: boolean;
    invalidError: boolean;
    validationError?: string;
    isDuplicate: boolean;
}

const PatternInput: React.FC<PatternInputProps> = ({
    regexPattern,
    onChange,
    onBlur,
    disabled,
    emptyError,
    invalidError,
    validationError,
    isDuplicate,
}) => {
    const { t } = useTranslation('regexPatternModal');
    return (
        <Column spacing={'xsmall'}>
            <Input
                value={regexPattern}
                onChange={onChange}
                onBlur={onBlur}
                label={t('regexPattern-fieldLabel')}
                disabled={disabled}
                error={emptyError || invalidError || isDuplicate}
                data-testid={'regex-modal-pattern'}
            />
            {emptyError && (
                <Alert type={'error'} size={'small'} data-testid={'regex-modal-pattern-error'}>
                    {t('regexPatternRequired-fieldError')}
                </Alert>
            )}
            {invalidError && validationError && (
                <Alert type={'error'} size={'small'} data-testid={'regex-modal-pattern-error'}>
                    {t(validationError)}
                </Alert>
            )}
            {isDuplicate && (
                <Alert type={'error'} size={'small'} data-testid={'regex-modal-pattern-duplicate-error'}>
                    {t('regexPatternDuplicate-fieldError')}
                </Alert>
            )}
        </Column>
    );
};

function buildFinalDescription(builderDesc: string, userNotes: string): string {
    const notes = userNotes.trim();
    if (!builderDesc) return notes;
    if (!notes) return builderDesc;
    return `${builderDesc} (${notes})`;
}

export interface RegexPatternModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (pattern: ResellerClaimRegexPattern) => void;
    selectedReseller: IAuthorizedReseller;
    userId: string;
    existingPatterns: ResellerClaimRegexPattern[];
}

export const RegexPatternModal: React.FC<RegexPatternModalProps> = ({
    isOpen,
    onClose,
    onSave,
    selectedReseller,
    userId,
    existingPatterns,
}) => {
    const { t } = useTranslation('regexPatternModal');
    const { t: tCommon } = useTranslation('common');

    const [regexDescription, setRegexDescription] = useState('');
    const [subResellerName, setSubResellerName] = useState('');
    const [regexPattern, setRegexPattern] = useState('');
    const [examplesText, setExamplesText] = useState('');
    const [segments, setSegments] = useState<PatternSegment[]>([{ id: 'seg-init-0', type: 'exactText', value: '' }]);
    const [patternTouched, setPatternTouched] = useState(false);
    const [examplesTouched, setExamplesTouched] = useState(false);

    const generatedRegex = useMemo(() => segmentsToRegex(segments), [segments]);
    const builderDescription = useMemo(() => segmentsToDescription(segments, t), [segments, t]);

    const handleSegmentsChange = useCallback((newSegments: PatternSegment[]) => {
        setSegments(newSegments);
        const regex = segmentsToRegex(newSegments);
        if (regex) {
            setRegexPattern(ensureAnchors(regex));
            setPatternTouched(true);
        } else {
            setRegexPattern('');
        }
    }, []);

    const handleManualPatternChange = useCallback((value: string) => {
        setRegexPattern(value);
        setSegments([]);
    }, []);

    const handleDescriptionChange = useCallback((value: string) => {
        setRegexDescription(value);
    }, []);

    const hasBuilderPattern = segments.length > 0;
    const regexValidation = useMemo(() => validateRegexPattern(regexPattern), [regexPattern]);

    const parsedExamples = useMemo(() => {
        if (!examplesText.trim()) return [];
        return examplesText
            .split(/\n/)
            .map((e) => e.trim())
            .filter((e) => e.length > 0);
    }, [examplesText]);

    const exampleValidation = useMemo(() => {
        if (parsedExamples.length === 0 || !regexPattern.trim() || !regexValidation.isValid) return { isValid: true };
        return validateExamplesMatchPattern(regexPattern, parsedExamples);
    }, [regexPattern, parsedExamples, regexValidation.isValid]);

    const isPatternEmpty = !regexPattern.trim();

    const isDuplicate = useMemo(() => {
        if (!regexPattern.trim() || !regexValidation.isValid) return false;
        const anchored = ensureAnchors(regexPattern);
        const sub = subResellerName.trim() || '';
        return existingPatterns.some((p) => p.regexPattern === anchored && (p.subResellerName || '') === sub);
    }, [regexPattern, subResellerName, existingPatterns, regexValidation.isValid]);

    const isSaveDisabled =
        isPatternEmpty ||
        parsedExamples.length === 0 ||
        !regexValidation.isValid ||
        !exampleValidation.isValid ||
        isDuplicate;

    const defaultSegment = (): PatternSegment => ({
        id: `seg-init-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: 'exactText',
        value: '',
    });

    const resetForm = useCallback(() => {
        setRegexDescription('');
        setSubResellerName('');
        setRegexPattern('');
        setExamplesText('');
        setSegments([defaultSegment()]);
        setPatternTouched(false);
        setExamplesTouched(false);
    }, []);

    const handleClearBuilder = useCallback(() => {
        setSegments([defaultSegment()]);
        setRegexPattern('');
        setRegexDescription('');
    }, []);

    const handleSave = useCallback(() => {
        setPatternTouched(true);
        setExamplesTouched(true);
        if (isSaveDisabled) return;
        onSave({
            resellerId: selectedReseller.resellerId,
            resellerName: selectedReseller.name,
            countryCode: selectedReseller.countryCode,
            regexDescription: buildFinalDescription(builderDescription, regexDescription),
            subResellerName: subResellerName.trim() || undefined,
            regexPattern: ensureAnchors(regexPattern),
            examples: parsedExamples,
            createdAt: '',
            lastUpdatedAt: '',
            updatedBy: userId,
        });
        resetForm();
    }, [
        isSaveDisabled,
        selectedReseller,
        builderDescription,
        regexDescription,
        subResellerName,
        regexPattern,
        parsedExamples,
        userId,
        onSave,
        resetForm,
    ]);

    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [onClose, resetForm]);

    const patternEmptyError = patternTouched && isPatternEmpty;
    const patternInvalidError = (patternTouched || hasBuilderPattern) && !isPatternEmpty && !regexValidation.isValid;

    return (
        <Modal
            title={t('addRegexPattern-modalTitle')}
            open={isOpen}
            onClose={handleClose}
            scrollContainer={'viewport'}
            closeLabel={tCommon('close-text')}
            data-testid={'regex-pattern-modal'}
        >
            <Column spacing={'400'}>
                <Input
                    value={subResellerName}
                    onChange={setSubResellerName}
                    label={t('subResellerName-fieldLabel')}
                    data-testid={'regex-modal-sub-reseller'}
                />

                <PatternBuilder
                    segments={segments}
                    onSegmentsChange={handleSegmentsChange}
                    generatedRegex={generatedRegex}
                />

                {hasBuilderPattern && (
                    <Row widths={'fit'}>
                        <Button
                            type={'tertiary'}
                            size={'small'}
                            onClick={handleClearBuilder}
                            data-testid={'regex-modal-clear-builder'}
                        >
                            {t('clearPattern-buttonLabel')}
                        </Button>
                    </Row>
                )}

                <PatternInput
                    regexPattern={regexPattern}
                    onChange={hasBuilderPattern ? undefined : handleManualPatternChange}
                    onBlur={() => setPatternTouched(true)}
                    disabled={hasBuilderPattern}
                    emptyError={patternEmptyError}
                    invalidError={patternInvalidError}
                    validationError={regexValidation.error}
                    isDuplicate={isDuplicate}
                />

                {hasBuilderPattern && (
                    <Column spacing={'200'}>
                        <Text type={'b200'} data-testid={'pattern-description-label'}>
                            {t('patternDescription-label')}
                        </Text>
                        <Text type={'b200'} color={'secondary'} data-testid={'pattern-description'}>
                            {(builderDescription as TranslatedString) || t('noSegments-placeholder')}
                        </Text>
                    </Column>
                )}

                <PatternTester
                    value={examplesText}
                    onChange={setExamplesText}
                    emptyError={parsedExamples.length === 0 && examplesTouched}
                    exampleValidation={exampleValidation}
                />

                <Column spacing={'xsmall'}>
                    <Textarea
                        value={regexDescription}
                        onChange={handleDescriptionChange}
                        label={t('regexDescription-fieldLabel-optional')}
                        rows={2}
                        data-testid={'regex-modal-description'}
                    />
                </Column>
            </Column>

            <ModalFooter>
                <Row alignmentHorizontal={'right'} widths={'fit'}>
                    <Button type={'secondary'} size={'small'} onClick={handleClose} data-testid={'regex-modal-cancel'}>
                        {tCommon('cancel-text')}
                    </Button>
                    <Button
                        type={'primary'}
                        size={'small'}
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        data-testid={'regex-modal-save'}
                    >
                        {tCommon('save-text')}
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};
